import React, { useState, useEffect } from 'react';
import api from '../api/client';
import toast from 'react-hot-toast';
import RiskExplanation from './RiskExplanation';
import { 
  CheckCircle, 
  Clock, 
  Phone, 
  Calendar, 
  Home, 
  Pill, 
  User, 
  AlertTriangle,
  ChevronRight,
  Loader2,
  X,
  Stethoscope,
  Heart,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

const ActionCenter = () => {
  const [riskData, setRiskData] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissedActionTypes, setDismissedActionTypes] = useState([]);
  const [showRiskDetails, setShowRiskDetails] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  useEffect(() => {
    fetchRiskData();
  }, []);

  const fetchRiskData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/risk-assessments/me');
      
      if (response.data.success) {
        setRiskData(response.data.data);
        setActions(response.data.data.recommendedActions || []);
      }
    } catch (error) {
      console.error('Error fetching risk data:', error);
      toast.error('Failed to load your action center');
    } finally {
      setLoading(false);
    }
  };

  const handleDismissAction = (actionType) => {
    setDismissedActionTypes(prev => [...prev, actionType]);
    toast.success('Action dismissed');
  };

  const handleActionResponse = async (action, response) => {
    try {
      // Send response to backend
      await api.post('/care-events/action-response', {
        actionType: action.actionType,
        patientResponse: response,
        timestamp: new Date()
      });

      toast.success('Response recorded');
      
      // Remove action from list
      setActions(actions.filter(a => a.actionType !== action.actionType));
    } catch (error) {
      console.error('Error recording action response:', error);
      toast.error('Failed to record response');
    }
  };

  const getRiskColor = (score) => {
    if (score >= 70) return 'text-danger-600';
    if (score >= 50) return 'text-orange-600';
    if (score >= 30) return 'text-yellow-600';
    return 'text-success-600';
  };

  const getRiskBg = (score) => {
    if (score >= 70) return 'bg-danger-50 border-danger-200';
    if (score >= 50) return 'bg-orange-50 border-orange-200';
    if (score >= 30) return 'bg-yellow-50 border-yellow-200';
    return 'bg-success-50 border-success-200';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingDown className="w-5 h-5 text-success-500" />;
    if (trend === 'worsening') return <TrendingUp className="w-5 h-5 text-orange-500" />;
    if (trend === 'critical') return <AlertTriangle className="w-5 h-5 text-danger-600" />;
    return <Minus className="w-5 h-5 text-surface-400" />;
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'urgent_clinical_review':
      case 'schedule_virtual_visit':
        return <Stethoscope className="w-5 h-5" />;
      case 'schedule_home_visit':
        return <Home className="w-5 h-5" />;
      case 'pharmacy_outreach':
      case 'medication_review':
        return <Pill className="w-5 h-5" />;
      case 'care_coordinator_outreach':
        return <User className="w-5 h-5" />;
      case 'self_care_guidance':
        return <Heart className="w-5 h-5" />;
      case 'follow_up_scheduling':
        return <Calendar className="w-5 h-5" />;
      default:
        return <Activity className="w-5 h-5" />;
    }
  };

  const getActionColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-danger-50 border-danger-200 text-danger-800';
      case 'high':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      default:
        return 'bg-primary-50 border-primary-200 text-primary-800';
    }
  };

  const filteredActions = actions.filter(a => !dismissedActionTypes.includes(a.actionType));

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-outline-variant/10 p-8 shadow-premium">
        <div className="flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Risk Score Card */}
      <div className={`rounded-2xl border-2 p-6 ${getRiskBg(riskData?.scores?.overall || 0)}`}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-surface-900">
              Your Health Status
            </h3>
            <p className="text-sm text-surface-600 mt-1">
              {riskData?.message || "We're monitoring your health to help you stay well at home."}
            </p>
          </div>
          <div className="text-right">
            <div className={`text-4xl font-bold ${getRiskColor(riskData?.scores?.overall || 0)}`}>
              {riskData?.scores?.overall || 0}
            </div>
            <p className="text-xs text-surface-500">Risk Score</p>
          </div>
        </div>

        {/* Trend */}
        {riskData?.trend && (
          <div className="mt-4 flex items-center gap-2">
            {getTrendIcon(riskData.trend)}
            <span className="text-sm font-medium capitalize">{riskData.trend}</span>
            <span className="text-xs text-surface-500">
              (Updated {riskData?.lastUpdated ? new Date(riskData.lastUpdated).toLocaleDateString() : 'recently'})
            </span>
          </div>
        )}

        {/* Risk Breakdown Toggle */}
        <div className="mt-4 flex items-center gap-4">
          <button
            onClick={() => setShowRiskDetails(!showRiskDetails)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            {showRiskDetails ? 'Hide Details' : 'View Risk Breakdown'}
          </button>
          {riskData?.assessmentId && (
            <button
              onClick={() => setShowExplanation(!showExplanation)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              {showExplanation ? 'Hide Explanation' : 'Understand My Score'}
            </button>
          )}
        </div>

        {/* Risk Breakdown */}
        {showRiskDetails && riskData?.scores && (
          <div className="mt-4 pt-4 border-t border-outline-variant/10">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-surface-500">Readmission Risk</p>
                <p className={`text-lg font-semibold ${getRiskColor(riskData.scores.readmissionRisk?.value || 0)}`}>
                  {riskData.scores.readmissionRisk?.value || 0}/100
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-surface-500">Deterioration Risk</p>
                <p className={`text-lg font-semibold ${getRiskColor(riskData.scores.deteriorationRisk?.value || 0)}`}>
                  {riskData.scores.deteriorationRisk?.value || 0}/100
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-surface-500">Medication Adherence</p>
                <p className={`text-lg font-semibold ${getRiskColor(riskData.scores.medicationAdherenceRisk?.value || 0)}`}>
                  {riskData.scores.medicationAdherenceRisk?.value || 0}/100
                </p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <p className="text-xs text-surface-500">Care Breakdown</p>
                <p className={`text-lg font-semibold ${getRiskColor(riskData.scores.careBreakdownRisk?.value || 0)}`}>
                  {riskData.scores.careBreakdownRisk?.value || 0}/100
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Risk Explanation (patient-friendly AI breakdown) */}
      {showExplanation && riskData?.assessmentId && (
        <RiskExplanation assessmentId={riskData.assessmentId} />
      )}

      {/* Recommended Actions */}
      {filteredActions.length > 0 && (
        <div className="bg-white rounded-2xl border border-outline-variant/10 p-6 shadow-premium">
          <h3 className="text-lg font-semibold text-surface-900 mb-4">
            Recommended Actions
          </h3>
          <div className="space-y-3">
            {filteredActions.map((action) => (
              <div
                key={action.actionType}
                className={`rounded-xl border p-4 ${getActionColor(action.priority)}`}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-white rounded-lg">
                    {getActionIcon(action.actionType)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">
                        {action.patientFacing?.title || action.actionType.replace(/_/g, ' ')}
                      </h4>
                      <span className="text-xs px-2 py-0.5 bg-white rounded-full capitalize">
                        {action.priority}
                      </span>
                    </div>
                    <p className="text-sm mt-1 opacity-90">
                      {action.patientFacing?.message || action.patientFacing?.guidance?.join(', ')}
                    </p>
                    
                    {/* Response Time */}
                    {action.patientFacing?.responseTime && (
                      <div className="flex items-center gap-1 mt-2 text-xs">
                        <Clock className="w-3 h-3" />
                        Expected response: {action.patientFacing.responseTime}
                      </div>
                    )}

                    {/* Action Options */}
                    {action.patientFacing?.actionOptions && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {action.patientFacing.actionOptions.map((option) => (
                          <button
                            key={option.id}
                            onClick={() => handleActionResponse(action, option.id)}
                            className="px-3 py-1.5 bg-white text-sm rounded-lg hover:bg-surface-50 transition-colors"
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Single Action Button */}
                    {action.patientFacing?.actionRequired && !action.patientFacing?.actionOptions && (
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleActionResponse(action, 'confirmed')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-white text-sm rounded-lg hover:bg-surface-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                          I understand
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDismissAction(action.actionType)}
                    className="p-1 hover:bg-black/5 rounded"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Actions State */}
      {filteredActions.length === 0 && riskData?.scores?.overall < 30 && (
        <div className="bg-success-50 border border-success-200 rounded-2xl p-6 text-center">
          <CheckCircle className="w-12 h-12 text-success-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-success-900">
            You're All Set!
          </h3>
          <p className="text-sm text-success-700 mt-1">
            No actions needed right now. Keep up with your care plan and report any changes.
          </p>
        </div>
      )}

      {/* Emergency Notice */}
      <div className="bg-danger-50 border border-danger-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-danger-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-danger-900">
              Medical Emergency?
            </p>
            <p className="text-sm text-danger-700">
              If you're experiencing severe symptoms, call 911 immediately or go to the nearest emergency room.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActionCenter;
